import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import {
    FaBuilding, FaPhone, FaEnvelope, FaGlobe, FaMapMarkerAlt, FaExternalLinkAlt,
    FaInstagram, FaFacebook, FaTwitter, FaYoutube, FaUniversity, FaCreditCard, FaUser,
    FaMoneyBillWave, FaEdit, FaSave, FaTimes, FaImage, FaQrcode, FaCopy,
} from "react-icons/fa";
import { MdBusiness, MdLocationOn } from "react-icons/md";
import { getFPOProfile, patchFPOProfile } from "../../services/fpoProfile";
import SearchableSelect from "../../components/inputFields/SearchableSelect";
import { resolveMediaUrl } from "../../helpers/media";

// ── Validation schema for editable fields ────────────────────────
const ProfileSchema = Yup.object().shape({
    zip_code:            Yup.string().max(10, "Max 10 characters"),
    phone:               Yup.string().max(20, "Max 20 characters"),
    alternate_phone:     Yup.string().max(20, "Max 20 characters"),
    email:               Yup.string().email("Invalid email"),
    website:             Yup.string().url("Must be a valid URL").nullable(),
    gstin:               Yup.string()
        .length(15, "GSTIN must be exactly 15 characters")
        .matches(/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/, "Invalid GSTIN format")
        .nullable()
        .transform(v => v === "" ? null : v),
    pan_no:              Yup.string()
        .length(10, "PAN must be exactly 10 characters")
        .matches(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, "Invalid PAN format (e.g. ABCDE1234F)")
        .nullable()
        .transform(v => v === "" ? null : v),
    bank_name:           Yup.string().max(100),
    bank_name_other:     Yup.string().when("bank_name", {
        is: "Other",
        then: schema => schema.required("Please enter the bank name").max(100, "Max 100 characters"),
        otherwise: schema => schema.nullable(),
    }),
    account_holder_name: Yup.string()
        .max(100, "Max 100 characters")
        .matches(/^[a-zA-Z\s.'-]*$/, "Only letters and spaces allowed")
        .nullable(),
    account_no:          Yup.string()
        .min(9, "Account number must be at least 9 digits")
        .max(18, "Account number cannot exceed 18 digits")
        .matches(/^[0-9]+$/, "Account number must contain only digits")
        .nullable()
        .transform(v => v === "" ? null : v),
    ifsc_code:           Yup.string()
        .length(11, "IFSC must be exactly 11 characters")
        .matches(/^[A-Z]{4}0[A-Z0-9]{6}$/, "Invalid IFSC format (e.g. SBIN0001234)")
        .nullable()
        .transform(v => v === "" ? null : v),
    upi_id:              Yup.string().max(50, "Max 50 characters"),
    invoice_prefix:      Yup.string().max(10, "Max 10 characters"),
    instagram_url:       Yup.string().url("Must be a valid URL").nullable(),
    facebook_url:        Yup.string().url("Must be a valid URL").nullable(),
    twitter_url:         Yup.string().url("Must be a valid URL").nullable(),
    youtube_url:         Yup.string().url("Must be a valid URL").nullable(),
    map_link:            Yup.string().url("Must be a valid URL").nullable(),
});

// ── Indian Banks List ─────────────────────────────────────────────
const INDIAN_BANKS = [
    "State Bank of India",
    "Punjab National Bank",
    "Bank of Baroda",
    "Canara Bank",
    "Union Bank of India",
    "Bank of India",
    "Indian Bank",
    "Central Bank of India",
    "Indian Overseas Bank",
    "UCO Bank",
    "Bank of Maharashtra",
    "Punjab & Sind Bank",
    "HDFC Bank",
    "ICICI Bank",
    "Axis Bank",
    "Kotak Mahindra Bank",
    "Yes Bank",
    "IDFC First Bank",
    "IndusInd Bank",
    "Federal Bank",
    "South Indian Bank",
    "Karur Vysya Bank",
    "City Union Bank",
    "Tamilnad Mercantile Bank",
    "Karnataka Bank",
    "Dhanlaxmi Bank",
    "Jammu & Kashmir Bank",
    "Nainital Bank",
    "RBL Bank",
    "Bandhan Bank",
    "CSB Bank",
    "AU Small Finance Bank",
    "Ujjivan Small Finance Bank",
    "Equitas Small Finance Bank",
    "Jana Small Finance Bank",
    "Suryoday Small Finance Bank",
    "ESAF Small Finance Bank",
    "Utkarsh Small Finance Bank",
    "Fincare Small Finance Bank",
    "IDBI Bank",
    "Saraswat Bank",
    "Abhyudaya Cooperative Bank",
    "Bassein Catholic Cooperative Bank",
    "Cosmos Cooperative Bank",
    "Shamrao Vithal Cooperative Bank",
    "Gramin Bank",
    "Other",
];

// ── Reusable field ────────────────────────────────────────────────
const FormRow = ({ label, name, type = "text", icon: Icon, disabled, placeholder }) => (
    <Field name={name}>
        {({ field }) => (
            <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">{label}</label>
                <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                        <Icon size={13} />
                    </span>
                    <input
                        {...field}
                        type={type}
                        placeholder={placeholder || label}
                        disabled={disabled}
                        className={`w-full pl-8 pr-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all duration-200
                            ${disabled ? "bg-gray-50 text-gray-400 cursor-not-allowed" : "bg-white text-gray-800 hover:border-gray-400"}
                            border-gray-200`}
                    />
                </div>
                <ErrorMessage name={name} component="p" className="text-red-500 text-xs mt-1" />
            </div>
        )}
    </Field>
);

// ── Section wrapper ───────────────────────────────────────────────
const Section = ({ title, icon: Icon, children }) => (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-5">
        <div className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-3 bg-gray-50/50">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <Icon className="text-primary text-sm" />
            </div>
            <h3 className="font-bold text-gray-700 text-sm">{title}</h3>
        </div>
        <div className="px-5 py-5 grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>
    </div>
);

// ── View-only row ─────────────────────────────────────────────────
const ViewRow = ({ label, value, isLink }) => {
    if (!value) return null;
    return (
        <div>
            <p className="text-xs text-gray-400 font-medium">{label}</p>
            {isLink ? (
                <a href={value} target="_blank" rel="noreferrer"
                    className="text-sm text-primary font-semibold hover:underline break-all">
                    {value}
                </a>
            ) : (
                <p className="text-sm text-gray-800 font-medium break-all">{value}</p>
            )}
        </div>
    );
};

// ── Bank Dropdown Row ─────────────────────────────────────────────
const BankDropdownRow = ({ disabled }) => (
    <Field name="bank_name">
        {({ field, form }) => {
            const isOther = field.value === "Other";
            return (
                <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Bank selector */}
                    <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-1">Bank Name</label>
                        <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10">
                                <FaUniversity size={13} />
                            </span>
                            <SearchableSelect
                                value={field.value}
                                onChange={(v) => form.setFieldValue("bank_name", v)}
                                disabled={disabled}
                                placeholder="— Select Bank —"
                                searchPlaceholder="Search banks…"
                                options={INDIAN_BANKS.map(b => ({ value: b, label: b }))}
                                className={`w-full pl-8 pr-8 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all duration-200
                                    ${disabled ? "bg-gray-50 text-gray-400 cursor-not-allowed" : "bg-white text-gray-800 hover:border-gray-400"}
                                    border-gray-200`}
                            />
                        </div>
                        <ErrorMessage name="bank_name" component="p" className="text-red-500 text-xs mt-1" />
                    </div>

                    {/* Custom bank name when "Other" is selected */}
                    {isOther && (
                        <Field name="bank_name_other">
                            {({ field: otherField }) => (
                                <div>
                                    <label className="block text-xs font-semibold text-gray-500 mb-1">Bank Name (Other)</label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                                            <FaUniversity size={13} />
                                        </span>
                                        <input
                                            {...otherField}
                                            type="text"
                                            maxLength={100}
                                            placeholder="Enter your bank name"
                                            disabled={disabled}
                                            className={`w-full pl-8 pr-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all duration-200
                                                ${disabled ? "bg-gray-50 text-gray-400 cursor-not-allowed" : "bg-white text-gray-800 hover:border-gray-400"}
                                                border-gray-200`}
                                        />
                                    </div>
                                    <ErrorMessage name="bank_name_other" component="p" className="text-red-500 text-xs mt-1" />
                                </div>
                            )}
                        </Field>
                    )}
                </div>
            );
        }}
    </Field>
);

// Helper to construct dynamic map embed URL with parsed zoom level
const getEmbedUrl = (link) => {
    if (!link || typeof link !== "string") return null;
    
    // Check if it's already an embed URL
    if (link.includes("output=embed") || link.includes("/embed")) {
        return link;
    }
    
    let query = null;
    let zoom = 19;
    
    // 1. Try to extract coordinate pair (e.g. @22.6964604,75.8659256)
    const coordMatch = link.match(/@([-0-9.]+),([-0-9.]+)/);
    if (coordMatch && coordMatch[1] && coordMatch[2]) {
        query = `${coordMatch[1]},${coordMatch[2]}`;
    } 
    // 2. Try to extract !3dlat!4dlng in Google Maps place URLs
    else {
        const placeCoordMatch = link.match(/!3d([-0-9.]+)!4d([-0-9.]+)/);
        if (placeCoordMatch && placeCoordMatch[1] && placeCoordMatch[2]) {
            query = `${placeCoordMatch[1]},${placeCoordMatch[2]}`;
        }
        // 3. Try to extract q= parameter
        else {
            const qMatch = link.match(/[?&]q=([^&]+)/);
            if (qMatch && qMatch[1]) {
                query = decodeURIComponent(qMatch[1]);
            }
            // 4. Try to extract place name e.g. /place/Rashail+Agro/
            else {
                const placeNameMatch = link.match(/\/place\/([^/@?]+)/);
                if (placeNameMatch && placeNameMatch[1]) {
                    query = decodeURIComponent(placeNameMatch[1].replace(/\+/g, ' '));
                }
            }
        }
    }
    
    // Extract zoom parameter (e.g. ,20z)
    const zoomMatch = link.match(/,([0-9.]+)z/);
    if (zoomMatch && zoomMatch[1]) {
        zoom = Math.round(parseFloat(zoomMatch[1]));
    }
    
    if (!query) return null;
    
    return `https://maps.google.com/maps?q=${encodeURIComponent(query)}&t=&z=${zoom}&ie=UTF8&iwloc=&output=embed`;
};

// ── Location Map Preview Component with Fallback ──────────────────
const LocationMapPreview = ({ mapLink }) => {
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        setHasError(false);
    }, [mapLink]);

    if (!mapLink) return null;

    const embedUrl = getEmbedUrl(mapLink);
    const renderMap = embedUrl && !hasError;

    return (
        <div className="sm:col-span-2 mt-2">
            <div className="flex items-center justify-between mb-1.5">
                <label className="block text-xs font-semibold text-gray-500">Location Map</label>
                <a
                    href={mapLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-primary font-semibold hover:underline"
                >
                    <FaMapMarkerAlt className="text-xs" />
                    Open in Google Maps
                    <FaExternalLinkAlt className="text-[10px] ml-0.5" />
                </a>
            </div>

            {renderMap ? (
                <div className="w-full h-[220px] rounded-xl overflow-hidden border border-gray-200 shadow-sm bg-gray-50 relative">
                    <iframe
                        title="FPO Location Map"
                        width="100%"
                        height="100%"
                        src={embedUrl}
                        frameBorder="0"
                        scrolling="no"
                        marginHeight="0"
                        marginWidth="0"
                        className="w-full h-full"
                        onError={() => setHasError(true)}
                    />
                </div>
            ) : (
                <div className="w-full p-4 rounded-xl border border-dashed border-gray-200 bg-gray-50/80 flex flex-col items-center justify-center text-center gap-2">
                    <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                        <FaMapMarkerAlt size={16} />
                    </div>
                    <p className="text-xs text-gray-600 font-medium">
                        Map preview is not available for this link.
                    </p>
                    <a
                        href={mapLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1.5 bg-primary text-white text-xs font-semibold rounded-lg hover:bg-primary/90 transition-colors inline-flex items-center gap-1.5 shadow-sm"
                    >
                        View on Google Maps
                        <FaExternalLinkAlt className="text-[10px]" />
                    </a>
                </div>
            )}
        </div>
    );
};

// ── Main Component ────────────────────────────────────────────────
const FPOProfile = () => {
    const [profile,  setProfile]  = useState(null);
    const [loading,  setLoading]  = useState(true);
    const [editing,  setEditing]  = useState(false);
    const [saving,   setSaving]   = useState(false);
    const [error,    setError]    = useState(null);

    // Image Upload States
    const [logoFile, setLogoFile] = useState(null);
    const [qrFile, setQrFile]     = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [qrPreview, setQrPreview]     = useState(null);

    // Lightbox (expanded image view)
    const [lightbox, setLightbox] = useState(null); // { src, alt } | null

    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setLogoFile(file);
            setLogoPreview(URL.createObjectURL(file));
        }
    };

    const handleQrChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setQrFile(file);
            setQrPreview(URL.createObjectURL(file));
        }
    };

    const resetFileState = () => {
        setLogoFile(null);
        setQrFile(null);
        setLogoPreview(null);
        setQrPreview(null);
    };

    // Fetch profile on mount
    const loadProfile = async () => {
        setLoading(true);
        setError(null);
        try {
            const res  = await getFPOProfile();
            const data = res?.data;
            if (data?.status === 200 || data?.status === "200") {
                setProfile(data.data);
            } else {
                setError(data?.message || "Failed to load profile.");
            }
        } catch (e) {
            setError("Unable to connect. Please check the server.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadProfile(); }, []);

    // ── PATCH handler ─────────────────────────────────────────────
    const handleSave = async (values) => {
        setSaving(true);
        try {
            // Only send changed/non-null fields
            const payload = new FormData();
            const editableKeys = [
                "zip_code",
                "phone","alternate_phone","email","website","gstin","pan_no",
                "bank_name","bank_name_other","account_holder_name","account_no","ifsc_code","upi_id","invoice_prefix",
                "instagram_url","facebook_url","twitter_url","youtube_url","map_link",
            ];
            editableKeys.forEach((key) => {
                if (key === "bank_name_other") return; // handled separately below
                if (values[key] !== undefined && values[key] !== null) {
                    payload.append(key, values[key]);
                }
            });
            // Resolve bank_name: if "Other", send the custom text
            if (values.bank_name === "Other") {
                payload.set("bank_name", values.bank_name_other || "");
            }
            if (logoFile) {
                payload.append("logo", logoFile);
            }
            if (qrFile) {
                payload.append("payment_qr", qrFile);
            }
            const res  = await patchFPOProfile(payload);
            const data = res?.data;
            if (data?.status === 200 || data?.status === "200" || res?.status === 200) {
                toast.success("Profile updated successfully! ✅");
                setEditing(false);
                resetFileState();
                await loadProfile();
            } else {
                toast.error(data?.message || "Update failed.");
            }
        } catch (e) {
            toast.error(e?.response?.data?.message || "Something went wrong.");
        } finally {
            setSaving(false);
        }
    };

    // ── Loading ───────────────────────────────────────────────────
    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="flex flex-col items-center gap-3">
                    <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                    <p className="text-gray-500 text-sm">Loading profile...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center h-64 gap-3">
                <p className="text-red-500 font-medium">{error}</p>
                <button onClick={loadProfile} className="px-4 py-2 bg-primary text-white rounded-lg text-sm">
                    Retry
                </button>
            </div>
        );
    }

    const fpo    = profile?.fpo || {};
    const logoUrl = resolveMediaUrl(profile?.logo);
    const qrUrl   = resolveMediaUrl(profile?.payment_qr);

    // Resolve saved bank_name: if it's not in list, treat as "Other"
    const knownBank = INDIAN_BANKS.slice(0, -1); // exclude "Other" sentinel
    const savedBank = profile?.bank_name || "";
    const isSavedOther = savedBank !== "" && !knownBank.includes(savedBank);

    // Initial form values
    const initialValues = {
        zip_code:            fpo?.zip_code                || "",
        phone:               profile?.phone               || "",
        alternate_phone:     profile?.alternate_phone     || "",
        email:               profile?.email               || "",
        website:             profile?.website             || "",
        gstin:               profile?.gstin               || "",
        pan_no:              profile?.pan_no              || "",
        bank_name:           isSavedOther ? "Other" : savedBank,
        bank_name_other:     isSavedOther ? savedBank : "",
        account_holder_name: profile?.account_holder_name || "",
        account_no:          profile?.account_no          || "",
        ifsc_code:           profile?.ifsc_code           || "",
        upi_id:              profile?.upi_id              || "",
        invoice_prefix:      profile?.invoice_prefix      || "",
        instagram_url:       profile?.instagram_url       || "",
        facebook_url:        profile?.facebook_url        || "",
        twitter_url:         profile?.twitter_url         || "",
        youtube_url:         profile?.youtube_url         || "",
        map_link:            profile?.map_link            || "",
    };

    const handleCopyDetails = () => {
        const addressParts = [
            fpo?.address,
            fpo?.block,
            fpo?.tehsil,
            fpo?.district,
            fpo?.state
        ].filter(Boolean);

        const lines = [
            `📋 *FPO PROFILE DETAILS*`,
            `----------------------------------------`,
            `🔹 *BASIC DETAILS*`,
            `• FPO Name: ${fpo?.name || "N/A"}`,
            `• Registration No: ${fpo?.registration_no || "N/A"}`,
            `• Registration Act: ${fpo?.registration_act || "N/A"}`,
            `• Date of Incorporation: ${fpo?.date_of_incorporation || "N/A"}`,
            `• Address: ${addressParts.join(", ") || "N/A"}`,
            `• GSTIN: ${profile?.gstin || "Not Provided"}`,
            `• PAN Number: ${profile?.pan_no || "Not Provided"}`,
            `----------------------------------------`,
            `🔸 *BANK DETAILS*`,
            `• Account Holder: ${profile?.account_holder_name || "Not Provided"}`,
            `• Bank Name: ${profile?.bank_name || "Not Provided"}`,
            `• Account Number: ${profile?.account_no || "Not Provided"}`,
            `• IFSC Code: ${profile?.ifsc_code || "Not Provided"}`,
            `• UPI ID: ${profile?.upi_id || "Not Provided"}`,
            `----------------------------------------`
        ];

        const textToCopy = lines.join("\n");
        navigator.clipboard.writeText(textToCopy)
            .then(() => {
                toast.success("FPO Details copied to clipboard! 📋");
            })
            .catch(() => {
                toast.error("Failed to copy details.");
            });
    };

    return (
        <div className="max-w-4xl mx-auto pb-12">
            {/* ── Page header ── */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 mb-5 flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden flex-shrink-0">
                        {logoUrl ? (
                            <img
                                src={logoUrl}
                                alt="FPO Logo"
                                className="w-full h-full object-contain p-1 cursor-pointer"
                                onClick={() => setLightbox({ src: logoUrl, alt: "FPO Logo" })}
                            />
                        ) : (
                            <FaBuilding className="text-gray-400 text-xl" />
                        )}
                    </div>
                    <div>
                        <h1 className="text-gray-800 text-base font-bold">{fpo?.name}</h1>
                        <p className="text-gray-500 text-xs">{fpo?.registration_no}</p>
                        <p className="text-gray-400 text-xs">{fpo?.district}, {fpo?.state}</p>
                    </div>
                </div>

                {!editing ? (
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={handleCopyDetails}
                            className="flex items-center gap-2 border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm font-medium px-4 py-2 rounded-xl transition-colors cursor-pointer"
                        >
                            <FaCopy className="text-xs" />
                            Copy Details
                        </button>
                        <button
                            type="button"
                            onClick={() => setEditing(true)}
                            className="flex items-center gap-2 bg-primary hover:bg-green-600 text-white text-sm font-medium px-4 py-2 rounded-xl transition-colors cursor-pointer"
                        >
                            <FaEdit className="text-xs" />
                            Edit Profile
                        </button>
                    </div>
                ) : (
                    <button
                        type="button"
                        onClick={() => { setEditing(false); resetFileState(); }}
                        className="flex items-center gap-2 border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm font-medium px-4 py-2 rounded-xl transition-colors cursor-pointer"
                    >
                        <FaTimes className="text-xs" />
                        Cancel
                    </button>
                )}
            </div>

            {/* ── Editable form via Formik ─────────────────────── */}
            <Formik
                initialValues={initialValues}
                validationSchema={ProfileSchema}
                enableReinitialize
                onSubmit={handleSave}
            >
                {({ isSubmitting, values }) => (
                    <Form>
                        {/* ── FPO Details (read-only info + tax info) ──────────────────────── */}
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 }}
                            className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-5"
                        >
                            <div className="px-5 py-3.5 border-b border-gray-100 bg-gray-50/50 flex items-center gap-3">
                                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                                    <FaBuilding className="text-primary text-sm" />
                                </div>
                                <h3 className="font-bold text-gray-700 text-sm">FPO Details</h3>
                                <span className="ml-2 text-[10px] bg-amber-100 text-amber-600 px-2 py-0.5 rounded-full font-semibold">
                                    Official Registration
                                </span>
                            </div>
                            <div className="px-5 py-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                                <ViewRow label="FPO Name"          value={fpo?.name} />
                                <ViewRow label="Registration No"   value={fpo?.registration_no} />
                                <ViewRow label="Registration Act"  value={fpo?.registration_act} />
                                <ViewRow label="Date of Incorp."   value={fpo?.date_of_incorporation} />
                                <ViewRow label="Address"           value={fpo?.address} />
                                <ViewRow label="Block"             value={fpo?.block} />
                                <ViewRow label="Tehsil"            value={fpo?.tehsil} />
                                <ViewRow label="District"          value={fpo?.district} />
                                <ViewRow label="State"             value={fpo?.state} />
                                {editing ? (
                                    <FormRow name="zip_code" label="Zip Code" icon={MdLocationOn} disabled={!editing} />
                                ) : (
                                    <ViewRow label="Zip Code" value={fpo?.zip_code} />
                                )}

                                {/* Tax & Business Details Shifted Here */}
                                <div className="sm:col-span-2 border-t border-gray-100 pt-4 mt-2">
                                    <h4 className="font-bold text-gray-700 text-xs mb-3 flex items-center gap-1.5 text-primary">
                                        <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                                        Tax Identification
                                    </h4>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        {editing ? (
                                            <>
                                                <FormRow name="gstin"  label="GSTIN"      icon={MdBusiness} disabled={!editing} />
                                                <FormRow name="pan_no" label="PAN Number" icon={MdBusiness} disabled={!editing} />
                                            </>
                                        ) : (
                                            <>
                                                <ViewRow label="GSTIN"      value={profile?.gstin || "Not Provided"} />
                                                <ViewRow label="PAN Number" value={profile?.pan_no || "Not Provided"} />
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </motion.div>

                        {/* Contact */}
                        <Section title="Contact Information" icon={FaPhone}>
                            <FormRow name="phone"           label="Phone"           icon={FaPhone}    disabled={!editing} />
                            <FormRow name="alternate_phone" label="Alternate Phone" icon={FaPhone}    disabled={!editing} />
                            <FormRow name="email"           label="Email"           icon={FaEnvelope} type="email" disabled={!editing} />
                            <FormRow name="website"         label="Website URL"     icon={FaGlobe}    disabled={!editing} />
                            <FormRow name="map_link"        label="Google Maps URL" icon={FaMapMarkerAlt} disabled={!editing} />

                            {/* Embedded Google Map Preview or Fallback Link */}
                            {values.map_link && <LocationMapPreview mapLink={values.map_link} />}
                        </Section>

                        {/* Social Media */}
                        <Section title="Social Media" icon={FaInstagram}>
                            <FormRow name="instagram_url" label="Instagram URL" icon={FaInstagram} disabled={!editing} />
                            <FormRow name="facebook_url"  label="Facebook URL"  icon={FaFacebook}  disabled={!editing} />
                            <FormRow name="twitter_url"   label="Twitter / X URL" icon={FaTwitter}   disabled={!editing} />
                            <FormRow name="youtube_url"   label="YouTube URL"   icon={FaYoutube}   disabled={!editing} />
                        </Section>

                        {/* Banking */}
                        <Section title="Banking Details" icon={FaUniversity}>
                            <FormRow name="account_holder_name" label="Account Holder Name" icon={FaUser}         disabled={!editing} placeholder="e.g. Ramesh Kumar" />
                            <BankDropdownRow disabled={!editing} />
                            <FormRow
                                name="account_no"
                                label="Account Number"
                                icon={FaCreditCard}
                                disabled={!editing}
                                placeholder="9–18 digit account number"
                            />
                            <FormRow
                                name="ifsc_code"
                                label="IFSC Code"
                                icon={MdBusiness}
                                disabled={!editing}
                                placeholder="e.g. SBIN0001234"
                            />
                        </Section>

                        {/* UPI Payment Details (grouped UPI ID and QR Code together) */}
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-5">
                            <div className="px-5 py-3.5 border-b border-gray-100 bg-gray-50/50 flex items-center gap-3">
                                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                                    <FaMoneyBillWave className="text-primary text-sm" />
                                </div>
                                <h3 className="font-bold text-gray-700 text-sm">UPI Payment Details</h3>
                            </div>
                            <div className="px-5 py-5 flex flex-col gap-6">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full">
                                    <FormRow name="upi_id" label="UPI ID" icon={FaMoneyBillWave} disabled={!editing} />
                                </div>

                                <div className="border-t border-gray-100 pt-4">
                                    <div className="flex flex-col items-center sm:items-start gap-3">
                                        <label className="block text-xs font-semibold text-gray-500">Payment QR Code</label>
                                        <div className="flex items-center gap-4 flex-col sm:flex-row w-full">
                                            <div className="w-24 h-24 rounded-xl border border-gray-200 overflow-hidden bg-gray-50 flex items-center justify-center flex-shrink-0 shadow-inner">
                                                {qrPreview ? (
                                                    <img
                                                        src={qrPreview}
                                                        alt="QR preview"
                                                        className="w-full h-full object-contain p-1 cursor-pointer"
                                                        onClick={() => setLightbox({ src: qrPreview, alt: "QR preview" })}
                                                    />
                                                ) : qrUrl ? (
                                                    <img
                                                        src={qrUrl}
                                                        alt="Payment QR"
                                                        className="w-full h-full object-contain p-1 cursor-pointer"
                                                        onClick={() => setLightbox({ src: qrUrl, alt: "Payment QR" })}
                                                    />
                                                ) : (
                                                    <FaQrcode className="text-gray-300 text-3xl" />
                                                )}
                                            </div>
                                            {editing && (
                                                <div className="flex flex-col gap-2 w-full text-center sm:text-left">
                                                    <label className="cursor-pointer bg-primary/10 hover:bg-primary/20 text-primary hover:text-green-700 font-semibold text-xs px-4 py-2.5 rounded-xl transition-colors text-center border border-primary/20 inline-block w-full sm:w-auto">
                                                        Upload QR Code
                                                        <input
                                                            type="file"
                                                            accept="image/*"
                                                            className="hidden"
                                                            onChange={handleQrChange}
                                                        />
                                                    </label>
                                                    <p className="text-[10px] text-gray-400">Suggest size: 500x500 px (Square)</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Invoice & Configurations */}
                        <Section title="Invoice Configuration" icon={MdBusiness}>
                            <FormRow name="invoice_prefix" label="Invoice Prefix"  icon={MdBusiness}      disabled={!editing} />
                        </Section>

                        {/* FPO Branding */}
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-5">
                            <div className="px-5 py-3.5 border-b border-gray-100 bg-gray-50/50 flex items-center gap-3">
                                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                                    <FaImage className="text-primary text-sm" />
                                </div>
                                <h3 className="font-bold text-gray-700 text-sm">FPO Branding</h3>
                            </div>
                            <div className="px-5 py-5">
                                {/* FPO Logo */}
                                <div className="flex flex-col items-center sm:items-start gap-3">
                                    <label className="block text-xs font-semibold text-gray-500">FPO Logo</label>
                                    <div className="flex items-center gap-4 flex-col sm:flex-row w-full">
                                        <div className="w-48 h-14 rounded-xl border border-gray-200 overflow-hidden bg-gray-50 flex items-center justify-center flex-shrink-0 shadow-inner">
                                            {logoPreview ? (
                                                <img
                                                    src={logoPreview}
                                                    alt="Logo preview"
                                                    className="w-full h-full object-contain p-2 cursor-pointer"
                                                    onClick={() => setLightbox({ src: logoPreview, alt: "Logo preview" })}
                                                />
                                            ) : logoUrl ? (
                                                <img
                                                    src={logoUrl}
                                                    alt="FPO Logo"
                                                    className="w-full h-full object-contain p-2 cursor-pointer"
                                                    onClick={() => setLightbox({ src: logoUrl, alt: "FPO Logo" })}
                                                />
                                            ) : (
                                                <FaBuilding className="text-gray-300 text-2xl" />
                                            )}
                                        </div>
                                        {editing && (
                                            <div className="flex flex-col gap-2 w-full text-center sm:text-left">
                                                <label className="cursor-pointer bg-primary/10 hover:bg-primary/20 text-primary hover:text-green-700 font-semibold text-xs px-4 py-2.5 rounded-xl transition-colors text-center border border-primary/20 inline-block w-full sm:w-auto">
                                                    Upload Logo
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        className="hidden"
                                                        onChange={handleLogoChange}
                                                    />
                                                </label>
                                                <p className="text-[10px] text-gray-400">Suggest size: 1920x404 px (Horizontal/Banner)</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Save button */}
                        {editing && (
                            <motion.div
                                initial={{ opacity: 0, y: 16 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="flex justify-end gap-3"
                            >
                                <button
                                    type="button"
                                    onClick={() => setEditing(false)}
                                    className="px-5 py-2.5 border border-gray-300 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmitting || saving}
                                    className="flex items-center gap-2 px-6 py-2.5 bg-primary hover:bg-green-600 text-white rounded-xl text-sm font-semibold shadow-lg shadow-primary/20 transition-colors disabled:opacity-60"
                                >
                                    {saving ? (
                                        <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                        </svg>
                                    ) : (
                                        <FaSave />
                                    )}
                                    {saving ? "Saving..." : "Save Changes"}
                                </button>
                            </motion.div>
                        )}
                    </Form>
                )}
            </Formik>

            {/* ── Image Lightbox ── */}
            <AnimatePresence>
                {lightbox && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[1200] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
                        onClick={() => setLightbox(null)}
                    >
                        <motion.img
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            src={lightbox.src}
                            alt={lightbox.alt}
                            onClick={(e) => e.stopPropagation()}
                            className="max-w-full max-h-[85vh] rounded-2xl shadow-2xl bg-white object-contain"
                        />
                        <button
                            onClick={() => setLightbox(null)}
                            className="absolute top-5 right-5 text-white/80 hover:text-white p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                        >
                            <FaTimes size={18} />
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default FPOProfile;
